import { useState, useEffect } from "react";
import { User, UserCog, Mail, Lock, Phone, X, CheckCircle, AlertCircle, Info, XCircle } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import ForgotPassword from "./ForgotPassword";
import apiService from '../config';
import { useProjectCategory } from '../Context/ProjectCategoryContext';  // ADD THIS
import { useLanguage } from '../StoreContext/LanguageContext'; // Import language hook
import VerifyOTP from "./VerifyOTP";

// Custom Notification Component
function Notification({ notification, onClose }) {
  useEffect(() => {
    if (notification.autoClose !== false) {
      const timer = setTimeout(() => {
        onClose();
      }, notification.duration || 4000);
      return () => clearTimeout(timer);
    }
  }, [notification, onClose]);

  const getNotificationStyles = () => {
    switch (notification.type) {
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          icon: CheckCircle,
          iconColor: 'text-green-600'
        };
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          icon: XCircle,
          iconColor: 'text-red-600'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          icon: AlertCircle,
          iconColor: 'text-yellow-600'
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          icon: Info,
          iconColor: 'text-blue-600'
        };
    }
  };

  const styles = getNotificationStyles();
  const IconComponent = styles.icon;

  return (
    <div className="fixed top-4 right-4 z-[10001] max-w-sm w-full animate-in slide-in-from-right duration-300">
      <div className={`${styles.bg} ${styles.border} border rounded-lg shadow-lg p-4`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <IconComponent className={`h-5 w-5 ${styles.iconColor}`} />
          </div>
          <div className="ml-3 flex-1">
            {notification.title && (
              <h3 className={`text-sm font-medium ${styles.text} mb-1`}>
                {notification.title}
              </h3>
            )}
            <p className={`text-sm ${styles.text}`}>
              {notification.message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onClose}
              className={`inline-flex rounded-md p-1.5 ${styles.text} hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main component that renders the authentication popup
export default function Login({ onClose, onLoginSuccess }) {
  // State to control which form is displayed
  const [activeForm, setActiveForm] = useState("user-signin");
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();

  // Function to show notifications
  const showNotification = (type, message, title = null, options = {}) => {
    setNotification({
      type,
      message,
      title,
      duration: options.duration || 4000,
      autoClose: options.autoClose !== false
    });
  };

  // Function to close notification
  const closeNotification = () => {
    setNotification(null);
  };

  // Function to switch between forms
  const switchForm = (formName) => {
    setActiveForm(formName);
    // Clear any existing notifications when switching forms
    setNotification(null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Background overlay */}
      <div
        className="fixed inset-0 bg-white bg-opacity-100 z-[9998]"
        onClick={onClose}
      ></div>

      {/* Notification */}
      {notification && (
        <Notification
          notification={notification}
          onClose={closeNotification}
        />
      )}

      {/* Popup modal */}
      <div className="relative z-[10000] max-w-md w-full mx-4">
        {/* Background accent layer like MMRDA design */}
        <div className="absolute -top-1 -bottom-1 -left-1 -right-1 bg-gradient-to-br from-blue-700 to-green-500 rounded-xl opacity-90 rotate-6"></div>
        {/* Main white card */}
        <div className="relative bg-white rounded-xl shadow-xl p-8 overflow-hidden max-h-[90vh] border-2 border-blue-300">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors duration-200 z-[10002]"
            aria-label="Close login popup"
          >
            <X size={24} />
          </button>

          {/* Scrollable content container */}
          <div
            className="overflow-y-auto max-h-[calc(90vh-1rem)]"
            style={{
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE 10+
            }}
          >
            <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

            {activeForm === "user-signin" && (
              <SignIn
                userType="user"
                onSwitchForm={switchForm}
                onClose={onClose}
                showNotification={showNotification}
                onLoginSuccess={onLoginSuccess}
              />
            )}

            {activeForm === "admin-signin" && (
              <SignIn
                userType="admin"
                onSwitchForm={switchForm}
                onClose={onClose}
                showNotification={showNotification}
                onLoginSuccess={onLoginSuccess}
              />
            )}

            {activeForm === "signup" && (
              <SignUp
                onSwitchForm={switchForm}
                showNotification={showNotification}
              />
            )}

            {activeForm === "forgot-password" && (
              <ForgotPassword
                onBackToSignIn={() => switchForm("user-signin")}
                showNotification={showNotification}
              />
            )}

            {activeForm === "verify-otp" && (
              <VerifyOTP
                onClose={onClose}
                showNotification={showNotification}
                onLoginSuccess={onLoginSuccess}
              />
            )}
          </div>
        </div>
      </div>

      {/* Global CSS */}
      <style jsx global>{`
      .hide-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }

      @keyframes slide-in-from-right {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .animate-in {
        animation-fill-mode: both;
      }

      .slide-in-from-right {
        animation-name: slide-in-from-right;
      }

      .duration-300 {
        animation-duration: 300ms;
      }
    `}</style>
    </div>
  );

}

// Sign In component for both user and admin
function SignIn({ userType, onSwitchForm, onClose, showNotification, onLoginSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { language, translations } = useLanguage();
  const { setProjectCategory } = useProjectCategory();  // ADD THIS

  const [formData, setFormData] = useState({
    identifier: "", // Single field for either email or mobile
    password: ""
  });

  // State for validation errors
  const [errors, setErrors] = useState({
    identifier: "",
    password: ""
  });

  // State for API loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  // ✅ OTP State Variables
  const [otpSent, setOtpSent] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }

    // Clear API error when user types
    if (apiError) {
      setApiError("");
    }
  };

  const handleSendOTP = async () => {
    // Validate identifier first
    if (!formData.identifier) {
      setErrors({ ...errors, identifier: "Email or mobile number is required" });
      return;
    }

    const isEmail = /\S+@\S+\.\S+/.test(formData.identifier);
    const isMobile = /^\d{10}$/.test(formData.identifier);

    if (!isEmail && !isMobile) {
      setErrors({ ...errors, identifier: "Please enter a valid email or 10-digit mobile number" });
      return;
    }

    try {
      setIsLoading(true);
      setApiError("");

      const otpData = {
        email: isEmail ? formData.identifier : "",
        mobile: !isEmail ? formData.identifier : ""
      };

      // Call the /postLogin endpoint to send OTP
      const response = await apiService.login(otpData);

      if (response.status === 200) {
        // Check if token is received (direct login without OTP)
        if (response?.token) {
          // Hard refresh on successful login
          window.location.reload();
        } else {
          // OTP is required - show OTP input
          setOtpSent(true);
          setShowOTPInput(true);
          showNotification(
            'success',
            response.message_detail || `OTP sent to your ${isEmail ? 'email' : 'mobile'}`,
            'OTP Sent'
          );
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      setApiError(error.message || 'Failed to send OTP');
      showNotification('error', error.message || 'Failed to send OTP', 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { identifier: "", password: "" };

    // Validate identifier
    if (!formData.identifier) {
      newErrors.identifier = "Email or mobile number is required";
      isValid = false;
    } else {
      const isEmail = /\S+@\S+\.\S+/.test(formData.identifier);
      const isMobile = /^\d{10}$/.test(formData.identifier);
      if (!isEmail && !isMobile) {
        newErrors.identifier = "Please enter a valid email or 10-digit mobile number";
        isValid = false;
      }
    }

    // OTP validation if OTP input is shown
    if (showOTPInput) {
      if (!otp) {
        setOtpError("OTP is required");
        isValid = false;
      } else if (otp.length !== 6) {
        setOtpError("OTP must be 6 digits");
        isValid = false;
      } else {
        setOtpError("");
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const isEmail = /\S+@\S+\.\S+/.test(formData.identifier);

      // Prepare verification data
      const verifyData = {
        email: isEmail ? formData.identifier : "",
        mobile: !isEmail ? formData.identifier : "",
        otp: otp
      };

      try {
        setIsLoading(true);
        setApiError("");

        // Call /postVerifyLoginOTP endpoint
        const response = await apiService.verifyLoginOTP(verifyData);

        console.log("OTP Verification Response:", response);

        // ✅ STORE TOKEN with correct key name that matches config.js
        const token = response.token || response.data?.token || response.access_token || response.data?.access_token;
        const refreshToken = response.refreshToken || response.data?.refreshToken || response.refresh_token || response.data?.refresh_token;

        if (token) {
          // ✅ CRITICAL: Use 'authToken' to match config.js getToken() method
          localStorage.setItem("authToken", token);

          // Also set token using apiService method if available
          if (apiService.setToken) {
            apiService.setToken(token);
          }

          console.log("Token stored as 'authToken':", token);
        } else {
          console.error("No token found in response:", response);
        }

        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
          console.log("Refresh token stored:", refreshToken);
        }

        // Check if user has a default project assigned
        if (!response.default_project || response.default_project === null) {
          showNotification(
            'warning',
            'No project has been assigned to your account. Please contact your administrator.',
            'No Project Assigned',
            { duration: 8000, autoClose: false }
          );

          const userData = {
            name: response.name || response.data?.name,
            email: response.email || response.data?.email,
            user_role: response.user_role || response.data?.user_role,
            default_project: null
          };
          localStorage.setItem("userData", JSON.stringify(userData));
          setIsLoading(false);
          return;
        }

        // Show success notification
        showNotification('success', 'Login successful! Redirecting...', 'Welcome back!');

        // ✅ Store complete user data - handle both direct response and response.data
        const responseData = response.data || response;

        const userData = {
          name: responseData.name,
          email: responseData.email,
          mobile: responseData.mobile,
          user_role: responseData.user_role,
          default_project: responseData.default_project,
          project_id: responseData.project_id,
          work_assign: responseData.work_assign || [],
          user_role_name: responseData.user_role_name || null,
          project_category: responseData.project_category
        };

        localStorage.setItem("userData", JSON.stringify(userData));
        localStorage.setItem("project_category", responseData.project_category);
        // setProjectCategory(responseData.project_category);

        console.log("Stored userData:", userData);
        console.log("Stored token:", token);

        // Fetch project details if available
        if (responseData.default_project) {
          apiService.getAllProjectsafterhomepage().then(result => {
            if (result.status === 200) {
              const projects = result.data.data || result.data;
              const project = Array.isArray(projects)
                ? projects.find(p => (p.id || p.project_id) === responseData.default_project)
                : projects;
              if (project) localStorage.setItem('selectedProject', JSON.stringify(project));
            }
          }).catch(() => { });

          apiService.get(`/getProposalDashboard?projectId=${responseData.default_project}`).then(result => {
            if (result.status === 200 && result.data[0]) {
              localStorage.setItem('selectedProposal', JSON.stringify(result.data[0]));
              localStorage.setItem('currentProposalId', String(result.data[0].acquisition_proposal_id));
            }
          }).catch(() => { });
        }

        // Handle navigation
        if (onLoginSuccess) {
          setTimeout(() => {
            onLoginSuccess();
            window.location.reload(); // ✅ refresh after login success
          }, 1500);
        } else {
          setTimeout(() => {
            if (responseData.user_role === null || responseData.user_role === undefined) {
              navigate('/GeneralObjection');
            } else {
              navigate('/dashboard');
            }
            if (onClose) onClose();
            window.location.reload(); // ✅ refresh after login success
          }, 1500);
        }

      } catch (error) {
        console.error("Login error:", error);
        setApiError(error.message || "Invalid OTP. Please try again.");
        showNotification('error', error.message || "Invalid OTP. Please try again.", 'Verification Failed');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Theme colors based on user type
  const theme = userType === "user" ? {
    gradient: "from-amber-500 to-yellow-600",
    hoverGradient: "from-amber-600 to-yellow-700",
    icon: "text-amber-600",
    focus: "focus:ring-amber-500 focus:border-amber-500",
    button: "bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700",
    switchColor: "text-blue-600 hover:text-blue-500",
    iconBg: "bg-amber-100"
  } : {
    gradient: "from-blue-600 to-blue-800",
    hoverGradient: "from-blue-700 to-blue-900",
    icon: "text-blue-600",
    focus: "focus:ring-blue-500 focus:border-blue-500",
    button: "bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900",
    switchColor: "text-amber-600 hover:text-amber-500",
    iconBg: "bg-blue-100"
  };

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-3">
          <div className={`${theme.iconBg} p-3 rounded-full shadow-md`}>
            {userType === "user" ? (
              <User size={24} className={theme.icon} />
            ) : (
              <UserCog size={24} className={theme.icon} />
            )}
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-1">
          {userType === "user" ? <span>{translations[language].SignIn}</span> : <span>{translations[language].AdminSignIn}</span>}
        </h2>
        <p className="text-gray-600 text-sm">
          {translations[language].EnterCredentials}
        </p>
      </div>

      {/* Error Message */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
          {apiError}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email/Mobile Input with Send OTP Button */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {translations[language].EmailOrMobile}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="identifier"
                value={formData.identifier}
                onChange={handleInputChange}
                disabled={otpSent}
                className={`w-full pl-10 pr-4 py-3 border ${errors.identifier ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none ${theme.focus} transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                placeholder="Email or 10-digit mobile"
              />
            </div>
            <button
              type="button"
              onClick={handleSendOTP}
              disabled={isLoading || otpSent}
              className={`px-6 py-3 ${theme.button} text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap`}
            >
              {otpSent ? "✓ Sent" : "Send OTP"}
            </button>
          </div>
          {errors.identifier && (
            <p className="mt-1 text-sm text-red-600">{errors.identifier}</p>
          )}
        </div>

        {/* OTP Input (shown after OTP is sent) */}
        {showOTPInput && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Enter OTP
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="password"
                inputMode="numeric"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only digits
                  if (value.length <= 6) {
                    setOtp(value);
                    setOtpError("");
                  }
                }}
                maxLength={6}
                className={`w-full pl-10 pr-4 py-3 border ${otpError ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none ${theme.focus} transition-colors duration-200`}
                placeholder="Enter 6-digit OTP"
              />
            </div>
            {otpError && (
              <p className="text-sm text-red-600">{otpError}</p>
            )}
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setShowOTPInput(false);
                setOtp("");
                handleSendOTP();
              }}
              disabled={isLoading}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium"
            >
              Resend OTP
            </button>
          </div>
        )}

        {/* Forgot Password Link */}
        <div className="flex items-center justify-between">
          <div></div>
          <button
            type="button"
            className="text-sm font-medium text-blue-600 hover:text-blue-500 cursor-pointer transition-colors duration-200"
            onClick={() => onSwitchForm("forgot-password")}
          >
            {translations[language].ForgotPassword}
          </button>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || !showOTPInput}
          className={`w-full py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${theme.button} 
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transform transition-all duration-200 hover:scale-[1.01]
          ${isLoading || !showOTPInput ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Verifying...
            </div>
          ) : (
            <span>{showOTPInput ? "Verify & Sign In" : "Enter OTP to Sign In"}</span>
          )}
        </button>
      </form>

      {/* Switch Forms and Social Login */}
      <div className="mt-6 space-y-4">
        {/* Switch User/Admin */}
        {/* <div className="text-center">
          {userType === "user" ? (
            <button
              type="button"
              onClick={() => onSwitchForm("admin-signin")}
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
            >
              <UserCog size={14} className="mr-1" />
              {translations[language].SwitchToAdmin}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onSwitchForm("user-signin")}
              className="inline-flex items-center text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors duration-200"
            >
              <User size={14} className="mr-1" />
              {translations[language].SwitchtoUserSignIn}
            </button>
          )}
        </div> */}

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            {translations[language].NoAccount}{" "}
            <button
              type="button"
              onClick={() => onSwitchForm("signup")}
              className="font-medium text-amber-600 hover:text-amber-500 transition-colors duration-200"
            >
              {translations[language].SignUp}
            </button>
          </p>
        </div>
      </div>
    </>
  );
}

// Sign Up component
function SignUp({ onSwitchForm, showNotification }) {
  const navigate = useNavigate();
  const { language, translations } = useLanguage();


  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    terms: false
  });

  // State for validation errors
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    terms: ""
  });

  // State for API loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }

    // Clear API error when user types
    if (apiError) {
      setApiError("");
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...errors };

    // Basic validations
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.mobile) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
      isValid = false;
    }

    if (!formData.terms) {
      newErrors.terms = "You must agree to the Terms of Service";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Prepare data for API call - addUser API only needs name, email, mobile
      const registrationData = {
        full_name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        mobile: formData.mobile
      };

      try {
        setIsLoading(true);
        setApiError("");

        // Using apiService.addNewUser -> POST /postAddUser
        const response = await apiService.addNewUser(registrationData);

        // On successful registration
        console.log("Registration successful:", response);

        // Replace alert with custom notification
        showNotification(
          'success',
          'Registration successful! You can now sign in.',
          'Registration Successful!',
          { duration: 6000 }
        );

        // Navigate to login page
        onSwitchForm("user-signin");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          mobile: "",
          terms: false
        });
      } catch (error) {
        console.error("Registration error:", error);
        setApiError(error.message || "An error occurred during registration. Please try again.");

        // Show error notification
        showNotification('error', error.message || "An error occurred during registration. Please try again.", 'Registration Failed');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle Google sign-up
  const handleGoogleSignUp = () => {
    console.log("Google sign-up initiated");
    // Replace alert with custom notification
    showNotification('info', 'Google OAuth integration is not implemented yet. This is a demo feature.', 'Google Sign Up');

    // In a real implementation, you would redirect to Google OAuth here
    // For demo purposes, we'll just navigate to dashboard
    setTimeout(() => {
      showNotification('success', 'Demo registration successful! Redirecting...', 'Demo Mode');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    }, 2000);
  };

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-3">
          <div className="bg-amber-100 p-3 rounded-full shadow-md">
            <User size={24} className="text-amber-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-1">{translations[language].CreateAccount}</h2>
        <p className="text-gray-600 text-sm">{translations[language].JoinPlatform}</p>
      </div>

      {/* Error Message */}
      {apiError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
          {apiError}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              {translations[language].FirstName}
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`w-full py-2 px-3 border rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all duration-200 ${errors.firstName ? "border-red-500" : "border-gray-300"
                }`}
              placeholder={translations[language].FirstName}
              autoComplete="given-name"
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              {translations[language].LastName}
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`w-full py-2 px-3 border rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all duration-200 ${errors.lastName ? "border-red-500" : "border-gray-300"
                }`}
              placeholder={translations[language].LastName}
              autoComplete="family-name"
            />
            {errors.lastName && (
              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Mobile Number */}
        <div className="space-y-1">
          <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
            {translations[language].MobileNumber}
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Phone size={16} className="text-gray-400" />
            </span>
            <input
              id="mobile"
              name="mobile"
              type="tel"
              value={formData.mobile}
              onChange={handleInputChange}
              className={`w-full py-2 pl-9 pr-3 border rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all duration-200 ${errors.mobile ? "border-red-500" : "border-gray-300"
                }`}
              placeholder={translations[language].MobileNumber}
              autoComplete="tel"
            />
          </div>
          {errors.mobile && (
            <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-1">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {translations[language].Email}
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Mail size={16} className="text-gray-400" />
            </span>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full py-2 pl-9 pr-3 border rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 transition-all duration-200 ${errors.email ? "border-red-500" : "border-gray-300"
                }`}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
          )}
        </div>

        {/* Terms Checkbox */}
        <div className="flex items-start py-2">
          <div className="flex items-center h-5 mt-1">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={formData.terms}
              onChange={handleInputChange}
              className="h-4 w-4 border-gray-300 rounded text-amber-600 focus:ring-amber-500"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="text-gray-600">
              {translations[language].TermsAgreement}
            </label>
          </div>
        </div>
        {errors.terms && (
          <p className="text-red-500 text-xs">{errors.terms}</p>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transform transition-all duration-200 hover:scale-[1.01] ${isLoading ? "opacity-70 cursor-not-allowed" : ""
            }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Creating Account...
            </div>
          ) : (
            <span>{translations[language].CreateAccountButton}</span>
          )}
        </button>

        {/* Sign In Link */}
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            {translations[language].AlreadyHaveAccount}{" "}
            <button
              type="button"
              onClick={() => onSwitchForm("user-signin")}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
            >
              {translations[language].SignIn}
            </button>
          </p>
        </div>

        {/* Divider */}
        {/* <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">OR</span>
          </div>
        </div> */}

        {/* Google Sign-up Button */}
        {/* <button
          type="button"
          onClick={handleGoogleSignUp}
          className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
            <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
              <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" />
              <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" />
              <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" />
              <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" />
            </g>
          </svg>
          Sign up with Google
        </button> */}
      </form>
    </>
  );
}